import React, { useEffect, useState } from 'react';
import { Select } from 'antd';
import { useAuth } from '../../../hooks/useAuth';
import { aiAdminApi, ModelBeliefsBrandRow } from '../../../services/ai-admin-api';

interface Props {
    value: number | null;
    onChange: (brandId: number | null, row: ModelBeliefsBrandRow | null) => void;
    // Only brands with a category label, so a read can be started; off for
    // attaching, where the brand may have no config yet.
    readableOnly?: boolean;
    placeholder?: string;
    autoFocus?: boolean;
}

// One searchable list of platform brands, by brand or organisation name.
export const ModelBeliefsBrandPicker: React.FC<Props> = ({
    value,
    onChange,
    readableOnly = false,
    placeholder = 'Search by brand or organisation',
    autoFocus,
}) => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const [rows, setRows] = useState<ModelBeliefsBrandRow[]>([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!jwt) return;
        let cancelled = false;
        const handle = setTimeout(async () => {
            setLoading(true);
            try {
                const response = await aiAdminApi.listModelBeliefsBrands(jwt, {
                    search,
                    configuredOnly: readableOnly,
                    limit: 100,
                });
                if (!cancelled) {
                    setRows(
                        readableOnly
                            ? response.brands.filter((b) => b.readable)
                            : response.brands,
                    );
                }
            } catch {
                if (!cancelled) setRows([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 250);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [jwt, search, readableOnly]);

    return (
        <Select
            showSearch
            autoFocus={autoFocus}
            style={{ width: '100%' }}
            value={value ?? undefined}
            placeholder={placeholder}
            loading={loading}
            filterOption={false}
            onSearch={setSearch}
            onChange={(brandId: number) =>
                onChange(brandId, rows.find((r) => r.brandId === brandId) ?? null)
            }
            options={rows.map((row) => ({
                value: row.brandId,
                label: `${row.brandName} · ${row.organisationName ?? `Org ${row.organisationId}`}${
                    row.templateType ? ` · ${row.templateType}` : ''
                }`,
            }))}
            notFoundContent={loading ? 'Searching' : 'No brands match'}
        />
    );
};
